/**
 * 閲覧を許可されていないページの縮退表示（EP-5 立ち読み範囲）。
 *
 * サーバはマニフェストに `allowedPages` を載せ、範囲外のページ成果物は 403 で拒む。
 * クライアントはそこへ要求を出さず、この装飾（デコレータ）が代わりに「会員限定」の
 * プレースホルダを返す。ページ総数は伏せないので、読者には続きの存在が見える。
 */
import type { LockedRange, ManifestSession, PageInfo } from '../core/manifest';
import { FULL_PAGE } from '../core/source';
import type { PageRaster, PageRasterRequest, SourceProvider } from '../core/source';
import type { Translate } from '../i18n/i18n';

/** ページが許可範囲に入っているか（範囲リストが空＝全ページ許可）。 */
export function isPageAllowed(ranges: readonly [number, number][] | undefined, page: number): boolean {
  if (!ranges || ranges.length === 0) return true;
  return ranges.some(([from, to]) => page >= from && page <= to);
}

/**
 * 遮断理由コード → 文言キーの接頭辞。
 *
 * 表に無い理由（リファラ制限など、閲覧者が次に取れる手が無いもの）は既定の
 * `locked.*`（会員限定）へ落ちる。ゲートとパスワードで文言が食い違っていた
 * 問題は、この対応表とサーバの `lockedPages` で解消する。
 */
const REASON_KEYS: Record<string, string> = {
  E_GATE_REQUIRED: 'locked.gate',
  E_PASSWORD_REQUIRED: 'locked.password',
  E_SCHEDULE_DENIED: 'locked.schedule',
  E_GROUP_DENIED: 'locked.group',
};

/** 遮断ページの案内。canvas の代替表示と DOM の代替テキストで同じ文言を使う。 */
export function lockedNotice(
  translate: Translate,
  ranges: readonly LockedRange[] | undefined,
  page: number,
): { title: string; hint: string } {
  // 重なったときは先に当たった理由（サーバの評価順）を採る。
  const hit = ranges?.find((range) => page >= range.from && page <= range.to);
  const prefix = (hit && REASON_KEYS[hit.code]) ?? 'locked';
  return { title: translate(`${prefix}.title`), hint: translate(`${prefix}.hint`) };
}

/** 既定のページ縦横比（マニフェストにページ情報が無いときの保険・A4 相当）。 */
const DEFAULT_ASPECT = 1.414;

export class LockedPagesProvider implements SourceProvider {
  private pages: readonly PageInfo[] = [];
  private allowed: readonly [number, number][] | undefined;
  private locked: readonly LockedRange[] | undefined;

  constructor(
    private readonly inner: SourceProvider,
    private readonly translate: Translate,
  ) {}

  async open(session: ManifestSession): Promise<void> {
    await this.inner.open(session);
    this.pages = session.current.pages;
    this.allowed = session.current.allowedPages;
    this.locked = session.current.lockedPages;
  }

  get pageCount(): number {
    return this.inner.pageCount;
  }

  renderPage(request: PageRasterRequest): Promise<PageRaster> {
    if (isPageAllowed(this.allowed, request.pageNo)) return this.inner.renderPage(request);
    return Promise.resolve(this.placeholder(request));
  }

  maxRasterWidth(pageNo: number): number {
    return this.inner.maxRasterWidth(pageNo);
  }

  maxEffectiveWidth(pageNo: number): number {
    return this.inner.maxEffectiveWidth(pageNo);
  }

  dispose(): Promise<void> {
    return this.inner.dispose();
  }

  /** 「会員限定」の代替ページを描く。 */
  private placeholder(request: PageRasterRequest): PageRaster {
    const info = this.pages.find((page) => page.no === request.pageNo);
    const aspect = info && info.width > 0 ? info.height / info.width : DEFAULT_ASPECT;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(request.targetWidth));
    canvas.height = Math.max(1, Math.round(canvas.width * aspect));

    const context = canvas.getContext('2d');
    if (context) {
      const { width, height } = canvas;
      context.fillStyle = '#e9e9ec';
      context.fillRect(0, 0, width, height);
      context.fillStyle = '#5a5a63';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      const unit = Math.max(12, Math.round(width / 18));
      // 文言は理由ごとに変わる。同じものが DOM 側（代替テキスト）にも出る。
      const notice = lockedNotice(this.translate, this.locked, request.pageNo);
      context.font = `bold ${unit}px sans-serif`;
      context.fillText(notice.title, width / 2, height / 2 - unit);
      context.font = `${Math.round(unit * 0.6)}px sans-serif`;
      context.fillText(notice.hint, width / 2, height / 2 + unit * 0.4);
    }

    return {
      pageNo: request.pageNo,
      bitmap: canvas,
      width: canvas.width,
      height: canvas.height,
      region: FULL_PAGE,
      close: () => {
        canvas.width = 0;
        canvas.height = 0;
      },
    };
  }
}
